import { createContext, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { accountsApi, profileApi } from '../api/endpoints';
import { ApiError, NetworkError } from '../api/httpClient';
import { describeError } from '../hooks/useAsync';
import type { Profile } from '../api/types';

export type SessionStatus = 'loading' | 'authenticated' | 'anonymous';

export interface SessionValue {
  status: SessionStatus;
  profile: Profile | null;
  signIn: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  /** Tras completar el perfil o dar de baja la cuenta, la sesión se vuelve a leer del servidor. */
  refresh: () => Promise<void>;
  forget: () => void;
  /**
   * Por qué el servidor rechazó la sesión, cuando lo dijo.
   *
   * Null en el caso normal —nunca hubo sesión, o venció—, porque ahí no hay nada que explicar: la
   * pantalla de ingreso ya dice qué hacer. Con texto cuando el rechazo tiene un motivo que la
   * persona no puede adivinar, como el 403 de la lista blanca.
   */
  rejection: string | null;
}

export const SessionContext = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<SessionStatus>('loading');
  const [profile, setProfile] = useState<Profile | null>(null);
  const [rejection, setRejection] = useState<string | null>(null);

  /**
   * No hay endpoint de "¿quién soy?": el perfil cumple esa función. Si la cookie sirve, devuelve
   * 200 y de paso trae lo único que la aplicación necesita saber antes de dibujar nada — si el
   * perfil está completo, porque sin eso no hay objetivo ni métricas derivadas.
   */
  const load = useCallback(async () => {
    try {
      const current = await profileApi.get();
      setProfile(current);
      setRejection(null);
      setStatus('authenticated');
    } catch (error) {
      if (error instanceof ApiError && error.isUnauthenticated) {
        setProfile(null);
        setRejection(null);
        setStatus('anonymous');
        return;
      }

      // Sin red no se puede afirmar que la sesión murió. Se asume viva y la aplicación arranca:
      // ese es justamente el escenario que RF-49 tiene que soportar.
      if (error instanceof NetworkError) {
        setStatus(profile === null ? 'anonymous' : 'authenticated');
        return;
      }

      /*
       * Cualquier otro rechazo deja la sesión afuera, PERO diciendo por qué.
       *
       * Acá había un `throw` dentro de una promesa que nadie espera —`void load()`—, así que el
       * error se perdía y `status` se quedaba en «loading» para siempre: la aplicación no
       * arrancaba nunca y la pantalla decía «Verificando la sesión» hasta que alguien cerrara la
       * pestaña. Lo comprobé con el 403 de la lista blanca del middleware.
       *
       * Colgarse no es una opción en ningún caso. Si el servidor rechaza y explica, se muestra la
       * explicación; si rechaza sin explicar, queda la frase genérica. Las dos cosas son mejores
       * que un spinner eterno.
       */
      setProfile(null);
      setRejection(describeError(error));
      setStatus('anonymous');
    }
  }, [profile]);

  useEffect(() => {
    void load();
    // Sólo al montar: `load` depende de `profile` y reejecutarlo acá sería un bucle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo<SessionValue>(
    () => ({
      status,
      profile,
      signIn: async (email, password) => {
        await accountsApi.signIn(email, password);
        setProfile(await profileApi.get());
        setStatus('authenticated');
      },
      register: async (email, password) => {
        // CU-13 paso 5 · el registro deja la sesión abierta; no se pide entrar de nuevo.
        await accountsApi.register(email, password);
        setProfile(await profileApi.get());
        setStatus('authenticated');
      },
      signOut: async () => {
        await accountsApi.signOut();
        setProfile(null);
        setStatus('anonymous');
      },
      refresh: async () => {
        setProfile(await profileApi.get());
      },
      forget: () => {
        setProfile(null);
        setRejection(null);
        setStatus('anonymous');
      },
      rejection,
    }),
    [status, profile, rejection],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}
